import { Reveal, SectionTag, Polaroid, StarDoodle } from "@/components/doodles";
import { scrollToId } from "@/lib/scrollTo";
import { ArrowRight } from "lucide-react";

const IMG = {
  gymnastics: "/img/gymnastics.jpeg",
  cheer: "/img/cheer.jpeg",
  crossfit: "/img/crossfit-field.webp",
  grad: "/img/grad.jpeg",
  comeback: "/img/comeback.jpeg",
};

const NODES = [
  {
    year: "THE EARLY YEARS",
    tag: "gymnastics • 8 years",
    copy: "Chalk, beam burns, and a work ethic that never left. Gymnastics built my foundation: discipline, body awareness, and a love for the process.",
    img: IMG.gymnastics,
    alt: "Rosa competing in gymnastics, mid floor routine",
    rotate: "-rotate-2",
  },
  {
    year: "THE HYPE ERA",
    tag: "mercer cheer",
    caption: "mercer cheer",
    copy: "I cheered at Mercer for a little bit — and it taught me that energy is contagious, and hype is a skill. (I bring both to every single session.)",
    img: IMG.cheer,
    alt: "Rosa cheering at Mercer with orange pom poms",
    rotate: "rotate-2",
  },
  {
    year: "2024 — TEEN SEMIFINALS",
    tag: "crossfit",
    copy: "Years of work, one qualification: the CrossFit Teen Semifinals. The highest level I'd ever reached — and proof that showing up compounds.",
    img: IMG.crossfit,
    alt: "Rosa competing at an outdoor CrossFit competition",
    rotate: "-rotate-2",
  },
  {
    year: "THE PLOT TWIST",
    tag: "the injury",
    caption: "graduation",
    copy: "Shortly after, an injury took me out of the gym for over a year. No PRs, no training — just learning patience, identity beyond the sport, and a faith that holds when everything else shakes.",
    img: IMG.grad,
    alt: "Rosa walking at her high school graduation",
    rotate: "rotate-2",
    quote: "the hardest year taught me the most.",
  },
  {
    year: "THE COMEBACK",
    tag: "now",
    copy: "I rebuilt from zero — smarter, stronger, and more grateful than before. This is the exact roadmap I now hand to you.",
    img: IMG.comeback,
    alt: "Rosa flexing her back in a red dress",
    rotate: "-rotate-2",
    quote: "back stronger. every rep earned.",
  },
];

export const Story = () => (
  <section id="story" className="py-24 sm:py-32 bg-ink text-cream relative overflow-hidden">
    <StarDoodle className="absolute top-16 right-[8%] w-8 h-8 text-gold" />
    <StarDoodle className="absolute bottom-24 left-[5%] w-6 h-6 text-pink" />
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <Reveal><SectionTag className="text-gold">MY STORY</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3 leading-[0.95]">
              THE <span className="hl-pink">COMEBACK</span> CHAPTER.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="font-hand text-2xl sm:text-3xl text-electric -rotate-1">spoiler: it ends really well</p>
        </Reveal>
      </div>

      <div className="relative mt-16 pl-10 sm:pl-14" data-testid="story-timeline">
        <div aria-hidden="true" className="absolute left-0 top-2 bottom-2 border-l-[3px] border-dashed border-cream/25" />
        <div className="flex flex-col gap-16">
          {NODES.map((n, i) => (
            <Reveal key={n.year} className="relative">
              <span className="absolute -left-[40px] sm:-left-[54px] top-1 w-5 h-5 rounded-full bg-pink border-[3px] border-cream" />
              <div className="grid sm:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="inline-block font-bebas tracking-[0.25em] text-xs px-3 py-1 border-[2.5px] border-cream/60 text-cream/80 -rotate-1">
                    {n.tag}
                  </span>
                  <h3 className="font-display font-extrabold uppercase text-3xl sm:text-4xl mt-3 leading-none">{n.year}</h3>
                  <p className="mt-4 text-base sm:text-lg font-semibold text-cream/70 leading-relaxed max-w-lg">{n.copy}</p>
                  {n.quote && (
                    <p className="font-hand text-3xl sm:text-4xl text-gold mt-4 -rotate-1">{n.quote}</p>
                  )}
                </div>
                {n.img ? (
                  <Polaroid src={n.img} alt={n.alt} caption={n.caption || n.tag} rotate={n.rotate} className="max-w-xs w-full justify-self-start sm:justify-self-end" />
                ) : (
                  <div className="hidden sm:block" />
                )}
              </div>
            </Reveal>
          ))}

          <Reveal className="relative">
            <span className="absolute -left-[40px] sm:-left-[54px] top-1 w-5 h-5 rounded-full bg-gold border-[3px] border-cream" />
            <div className="bg-pink text-cream border-[3px] border-cream shadow-[8px_8px_0_rgba(249,248,246,0.9)] p-8 sm:p-10 -rotate-1" data-testid="story-final-node">
              <p className="font-bebas tracking-[0.25em] text-sm text-cream/80">AND THAT'S WHY</p>
              <h3 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-2 leading-[0.95]">
                EVOLVE WITH ROSA WAS BORN.
              </h3>
              <p className="font-hand text-2xl sm:text-3xl mt-3">your chapter starts right here →</p>
              <button onClick={() => scrollToId("contact")} data-testid="story-cta" className="btn-pop bg-cream text-ink px-6 py-3.5 mt-6 text-sm sm:text-base">
                Start your chapter <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
